import { GetWorkplaceList, GetAllWorkplaces } from 'core/API/workplaces';
import { getTokenFromLocalStorage } from 'core/services/axiosInterceptors';
import { useAuth } from 'module/auth/context/AuthContext';
import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { IWorkplaces } from '../interface/WorkplacesInterface';
import { Workplace } from 'module/workplaces/interface/WorkplacesInterface';

interface IRQuery {
	error: null;
	isError: false;
	isIdle: true;
	isLoading: false;
	isLoadingError: false;
	isRefetchError: false;
	isSuccess: false;
	status: 'idle';
	dataUpdatedAt: number;
	errorUpdatedAt: number;
}

interface IQuery extends IRQuery {
	data: IWorkplaces;
	workplaceId: number;
	workplace: Workplace;
	timezone: string;
	timezones: string;
	firstDayOfWeek: number;
	isTradeSelected: boolean;
	setIsTradeSelected: (arg0: boolean) => void;
	setFirstDay: (arg0: number) => void;
	setWorkplaceIDHandler: (arg0: number, arg1: string, arg2: string) => void;
	organization: any;
	// Legal agreement modal state (exposed so screens can render modal where appropriate)
	showLegal?: boolean;
	setShowLegal?: (v: boolean) => void;
	currentUserId?: string | number;
	activeIdentifier?: string | null;
}

const WorkplacesContext = React.createContext<any>(undefined!);

export function WorkplacesContextProvider({
	children,
}: {
	children: React.ReactNode;
}): JSX.Element {
	const auth = useAuth();

	const [isTradeSelected, setIsTradeSelected] = useState(false);
	const [firstDayOfWeek, setFirstDay] = useState(1);

	// Fetch user's accessible workplaces (limited details)
	const { ...rest } = useQuery(['workplaces'], GetWorkplaceList, {
		enabled: auth.isLoggedin(),
		staleTime: Infinity,
	});

	// Fetch complete workplace details for all workplaces
	const { data: allWorkplacesData } = useQuery(
		['workplaces-all-details'],
		GetAllWorkplaces,
		{
			enabled: auth.isLoggedin(),
			staleTime: Infinity,
		}
	);

	// Create a map of complete workplace details by id for efficient lookup
	const workplacesDetailsMap = useMemo(() => {
		if (!allWorkplacesData) return new Map();
		return new Map(allWorkplacesData.map((wp: any) => [wp.id, wp]));
	}, [allWorkplacesData]);

	// Merge user's accessible workplaces with complete details
	const enrichedWorkplacesData = useMemo(() => {
		if (!rest.data?.items || workplacesDetailsMap.size === 0) return rest.data;

		const enrichedItems = rest.data.items.map((item) => {
			const completeDetails = workplacesDetailsMap.get(item.id);
			if (completeDetails) {
				// Merge complete details with user's workplace item
				return {
					...completeDetails,
					...item,
					// Preserve specific fields from the user's workplaces list
					workplace_unit_number: item.workplace_unit_number,
				};
			}
			return item;
		});

		return {
			...rest.data,
			items: enrichedItems,
		};
	}, [rest.data, workplacesDetailsMap]);

	const { workplaceId, setWorkplaceIDHandler, timezone, timezones } = useProviderFunctions();
	const workplace = useMemo(() => {
		if (enrichedWorkplacesData) {
			const wp = enrichedWorkplacesData.items
				.map((item) => ({
					...item,
					id: item.id,
					manager: true,
					role: [],
					workplaceUnitNumber: item.workplace_unit_number,
				}))
				.find((item) => {
					return item.id === workplaceId;
				});

			return wp || {};
		}
	}, [enrichedWorkplacesData, workplaceId]);

	return (
		<WorkplacesContext.Provider
			value={{
				...rest,
				data: enrichedWorkplacesData,
				workplaceId,
				workplace,
				timezone,
				timezones,
				setWorkplaceIDHandler,
				isTradeSelected,
				firstDayOfWeek,
				setFirstDay,
				setIsTradeSelected,
			}}
		>
			{children}
		</WorkplacesContext.Provider>
	);
}

function useProviderFunctions() {
	const queryClient = useQueryClient();
	const [workplaceId, setworkplaceId] = useState<number>(() => {
		const workplaceId = getTokenFromLocalStorage('workplace');
		return workplaceId;
	});

	const [timezone, setTimezone] = useState<string>(() => {
		const timezoneLS = getTokenFromLocalStorage('timezone');
		return timezoneLS;
	});
	const [timezones, setTimezones] = useState<string>(() => {
		const timezoneLS = getTokenFromLocalStorage('timeZones');
		return timezoneLS;
	});

	const setWorkplaceIDHandler = async (id: number, timezone: string, timeZones: string) => {
		setTimeout(async () => {
			const data = await JSON.parse(localStorage.getItem('BYOD-app')!);
			const previousWorkplaceId = data?.workplace;
			
			data.workplace = id;
			data.timezone = timezone;
			data.timeZones = timeZones;
			localStorage.setItem('BYOD-app', JSON.stringify(data));
			// console.log(data?.workplace, "line 96");
			localStorage.setItem('workplace-id', JSON.stringify(data?.workplace));
			
			// Clear GameMaster cache when switching workplaces
			// Clear both previous and new workplace cache to ensure fresh data
			try {
				const { clearGameMasterCache } = await import('core/utils/localStorageUtils');
				
				// Clear cache for previous workplace if it exists and is different
				if (previousWorkplaceId && previousWorkplaceId !== id) {
					clearGameMasterCache(previousWorkplaceId, queryClient);
				}
				
				// Also clear cache for new workplace to ensure fresh data
				clearGameMasterCache(id, queryClient);
				
				// Clear all GameMaster-related React Query cache
				queryClient.invalidateQueries(['gameMaster']);
				queryClient.invalidateQueries(['screenGroups']);
				queryClient.invalidateQueries(['sportsPreferences']);
				queryClient.invalidateQueries(['gracenoteLineups']);
				queryClient.invalidateQueries(['gracenoteDefaults']);
			} catch (e) {
				console.warn('Failed to clear GameMaster cache:', e);
			}
			
			setworkplaceId(id);
			setTimezone(timezone);
			setTimezones(timeZones);
		}, 50);
	};

	return { workplaceId, timezone, setWorkplaceIDHandler, timezones };
}

export const useWorkplacesContext = (): IQuery => React.useContext(WorkplacesContext);
